// src/hooks/useBrandState.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { brandsAPI } from '../api';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Brand state management hook
 * Extracted from BrandPage.js to enable reuse in TrendPage
 */
export const useBrandState = () => {
  const { translate, language } = useLanguage();

  // Category mapping - memoized to prevent recreation on every render
  const CATEGORY_MAP = useMemo(() => ({
    [translate('brandPage.categories.all')]: '',
    [translate('brandPage.categories.skincare')]: 'CM01',
    [translate('brandPage.categories.bodycare')]: 'CM02',
    [translate('brandPage.categories.haircare')]: 'CM03',
    [translate('brandPage.categories.suncare')]: 'CM04',
    [translate('brandPage.categories.cleansing')]: 'CM05'
  }), [translate]);

  // Categories array - memoized
  const categories = useMemo(() => [
    translate('brandPage.categories.all'),
    translate('brandPage.categories.skincare'),
    translate('brandPage.categories.bodycare'),
    translate('brandPage.categories.haircare'),
    translate('brandPage.categories.suncare'),
    translate('brandPage.categories.cleansing')
  ], [translate]);

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [params, setParams] = useState({
    page: 1,
    perPage: 20,
    sortBy: 'search_trend',
    sortOrder: 'desc',
    category: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  /**
   * Get category name from category code
   */
  const getCategoryName = useCallback((categoryCode) => {
    const categoryMap = {
      'CM01': translate('brandPage.categories.skincare'),
      'CM02': translate('brandPage.categories.bodycare'),
      'CM03': translate('brandPage.categories.haircare'),
      'CM04': translate('brandPage.categories.suncare'),
      'CM05': translate('brandPage.categories.cleansing'),
      '': translate('brandPage.categories.all')
    };
    return categoryMap[categoryCode] || translate('brandPage.categories.all');
  }, [translate]);

  /**
   * Fetch brand rankings
   */
  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);

      const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
      const apiParams = {
        ...params,
        lang: apiLanguage
      };

      const response = await brandsAPI.getBrandRankings(apiParams);

      setBrands(response.items);
      setPagination({
        total: response.total,
        totalPages: response.total_pages,
        hasNext: response.has_next,
        hasPrev: response.has_prev
      });
    } catch (error) {
      console.error(translate('brandPage.errorMessages.loadFailed'), error);
    } finally {
      setLoading(false);
    }
  }, [params, language, translate]);

  /**
   * Search brands
   */
  const handleSearch = useCallback(async (term) => {
    try {
      setLoading(true);
      setIsSearchMode(true);
      setSearchTerm(term);

      const currentCategory = getCategoryName(params.category);
      const processedCategory = currentCategory === translate('brandPage.categories.all') ? '' : currentCategory;

      const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
      const response = await brandsAPI.searchBrands(term, {
        sort_by: params.sortBy,
        category: processedCategory,
        lang: apiLanguage
      });

      setBrands(response.items);
      setPagination({
        total: response.total,
        totalPages: response.total_pages,
        hasNext: response.has_next,
        hasPrev: response.has_prev
      });
    } catch (error) {
      console.error(translate('brandPage.errorMessages.searchFailed'), error);
    } finally {
      setLoading(false);
    }
  }, [params, language, translate, getCategoryName]);

  /**
   * Handle category change
   */
  const handleCategoryChange = useCallback(async (categoryName) => {
    if (isSearchMode) {
      try {
        setLoading(true);
        const processedCategory = categoryName === translate('brandPage.categories.all') ? '' : categoryName;

        setParams(prev => ({
          ...prev,
          category: CATEGORY_MAP[categoryName]
        }));

        const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
        const response = await brandsAPI.searchBrands(searchTerm, {
          sort_by: params.sortBy,
          category: processedCategory,
          lang: apiLanguage
        });

        setBrands(response.items);
        setPagination({
          total: response.total,
          totalPages: response.total_pages,
          hasNext: response.has_next,
          hasPrev: response.has_prev
        });
      } catch (error) {
        console.error(translate('brandPage.errorMessages.searchFailed'), error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setParams(prev => ({
      ...prev,
      page: 1,
      category: CATEGORY_MAP[categoryName]
    }));
  }, [isSearchMode, searchTerm, params.sortBy, language, translate, CATEGORY_MAP]);

  /**
   * Handle sort change
   */
  const handleSort = useCallback(async (sortField) => {
    if (isSearchMode) {
      try {
        setLoading(true);
        const currentCategory = getCategoryName(params.category);
        const processedCategory = currentCategory === translate('brandPage.categories.all') ? '' : currentCategory;

        setParams(prev => ({
          ...prev,
          sortBy: sortField,
          sortOrder: 'desc'
        }));

        const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
        const response = await brandsAPI.searchBrands(searchTerm, {
          sort_by: sortField,
          category: processedCategory,
          lang: apiLanguage
        });

        setBrands(response.items);
        setPagination({
          total: response.total,
          totalPages: response.total_pages,
          hasNext: response.has_next,
          hasPrev: response.has_prev
        });
      } catch (error) {
        console.error(translate('brandPage.errorMessages.searchFailed'), error);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (params.sortBy === sortField) {
      return;
    }

    setParams(prev => ({
      ...prev,
      sortBy: sortField,
      sortOrder: 'desc'
    }));
  }, [isSearchMode, searchTerm, params, language, translate, getCategoryName]);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((newPage) => {
    if (isSearchMode) return;

    setParams(prev => ({
      ...prev,
      page: newPage
    }));

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [isSearchMode]);

  /**
   * Reset search
   */
  const handleReset = useCallback(() => {
    setIsSearchMode(false);
    setSearchTerm('');
    setParams(prev => ({
      ...prev,
      page: 1,
      sortBy: 'search_trend',
      sortOrder: 'desc',
      category: ''
    }));
  }, []);

  // Fetch brands on params change (when not in search mode)
  useEffect(() => {
    if (!isSearchMode) {
      fetchBrands();
    }
  }, [params, isSearchMode, language, fetchBrands]);

  return {
    // State
    brands,
    loading,
    isSearchMode,
    searchTerm,
    params,
    pagination,
    categories,
    CATEGORY_MAP,
    // Methods
    fetchBrands,
    handleSearch,
    handleCategoryChange,
    handleSort,
    handlePageChange,
    handleReset,
    getCategoryName
  };
};

export default useBrandState;
